/** Number formatting for the usage cards. Locale-aware, no decimals for counts. */
const countFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const oneDecimal = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
})

export function formatCount(value: number): string {
  return countFormat.format(value)
}

/** `99.2` → `99.2`, `100` → `100.0`; the `%` unit is rendered separately. */
export function formatPercent(value: number): string {
  return oneDecimal.format(value)
}

/** Whole milliseconds; the `ms` unit is rendered separately. */
export function formatMs(value: number): string {
  return countFormat.format(Math.round(value))
}

export interface Delta {
  direction: 'up' | 'down'
  /** Absolute change, already formatted. */
  value: string
  /** Signed change before formatting, for tone decisions. */
  raw: number
}

/** Relative change in percent between two totals; null without a usable baseline. */
export function percentChange(current: number, previous: number | null): Delta | null {
  if (previous == null || previous === 0) return null
  const raw = ((current - previous) / previous) * 100
  if (!Number.isFinite(raw) || Math.abs(raw) < 0.05) return null
  return { direction: raw > 0 ? 'up' : 'down', value: `${oneDecimal.format(Math.abs(raw))}%`, raw }
}

/** Absolute change in percentage points; null without a baseline. */
export function pointChange(current: number | null, previous: number | null): Delta | null {
  if (current == null || previous == null) return null
  const raw = current - previous
  if (Math.abs(raw) < 0.05) return null
  return { direction: raw > 0 ? 'up' : 'down', value: `${oneDecimal.format(Math.abs(raw))}%`, raw }
}

/** Absolute change in milliseconds; null without a baseline. */
export function msChange(current: number | null, previous: number | null): Delta | null {
  if (current == null || previous == null) return null
  const raw = Math.round(current - previous)
  if (raw === 0) return null
  return {
    direction: raw > 0 ? 'up' : 'down',
    value: `${countFormat.format(Math.abs(raw))} ms`,
    raw
  }
}
